import { Box } from '@mui/material'
import { useCallback, useRef, useState } from 'react'

interface P {
    left: React.ReactNode
    right: React.ReactNode
    initialLeftWidth?: number
    minLeftWidth?: number
    minRightWidth?: number
}

const VerticalSplitView = ({ left, right, initialLeftWidth = 50, minLeftWidth = 200, minRightWidth = 200 }: P) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [leftPercent, setLeftPercent] = useState(initialLeftWidth)
    const isDraggingRef = useRef(false)

    const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        isDraggingRef.current = true
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
    }, [])

    const handlePointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!isDraggingRef.current) return
            const container = containerRef.current
            if (!container) return
            const rect = container.getBoundingClientRect()
            const containerWidth = rect.width
            const newLeftPx = event.clientX - rect.left
            if (newLeftPx < minLeftWidth || containerWidth - newLeftPx < minRightWidth) return
            setLeftPercent((newLeftPx / containerWidth) * 100)
        },
        [minLeftWidth, minRightWidth]
    )

    const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        isDraggingRef.current = false
        event.currentTarget.releasePointerCapture(event.pointerId)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
    }, [])

    return (
        <Box ref={containerRef} sx={{ display: 'flex', height: '100%' }}>
            {/* LEFT PANEL */}
            <Box
                sx={{
                    width: right ? `${leftPercent}%` : '100%',
                    minWidth: minLeftWidth,
                    height: '100%'
                }}>
                {left}
            </Box>

            {right && (
                <>
                    <Box
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        sx={{
                            'width': 6,
                            'cursor': 'col-resize',
                            'bgcolor': 'divider',
                            '&:hover': { bgcolor: 'primary.main' }
                        }}
                    />

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: minRightWidth,
                            height: '100%'
                        }}>
                        {right}
                    </Box>
                </>
            )}
        </Box>
    )
}

export default VerticalSplitView
